import { useRef, useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound } from 'lucide-react';
import { useTranslation } from '../../i18n/useTranslation';
import { Badge, Button, Card, EmptyState, Stack, Text, Textarea } from '../../components/atoms';
import { Alert } from '../../components/atoms/Alert/Alert';
import { LiveRegion } from '../../components/atoms/LiveRegion/LiveRegion';
import { LoadErrorState } from '../../components/feedback/LoadErrorState/LoadErrorState';
import { AppLayout } from '../../components/templates/AppLayout/AppLayout';
import { PageContent } from '../../components/templates/PageContent/PageContent';
import { PageHeader } from '../../components/templates/PageHeader/PageHeader';
import { Section } from '../../components/templates/Section/Section';
import { Header } from '../../components/layout/Header';
import { Sidebar } from '../../components/Sidebar';
import { ApiError } from '../../lib/apiClient';
import { clearLicenseRequired } from '../../lib/licenseStatus';
import {
  LICENSE_EXPIRY_WARNING_DAYS,
  applyLicenseKey,
  daysUntil,
  fetchOwnLicense,
  ownLicenseQueryKey,
  type OwnLicense,
} from '../../services/licenseService';
import './LicensePage.css';

function formatLongDate(iso: string | null, language: string): string {
  if (iso === null) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(language, { day: 'numeric', month: 'long', year: 'numeric' }).format(date);
}

/** Translation key of the field/alert message for a failed activation. */
function errorKey(err: unknown): { key: string; field: boolean } {
  const code = err instanceof ApiError ? err.code : '';
  switch (code) {
    case 'INVALID_CODE':
    case 'VALIDATION_ERROR':
      return { key: 'licensePage.errors.invalid', field: true };
    case 'EMAIL_MISMATCH':
      return { key: 'licensePage.errors.otherAccount', field: true };
    case 'ALREADY_USED':
      return { key: 'licensePage.errors.used', field: true };
    case 'USER_ALREADY_LICENSED':
    case 'NO_ACTIVE_LICENSE':
      return { key: 'licensePage.errors.stateChanged', field: false };
    case 'MISSING_SECRET':
      return { key: 'licensePage.errors.unavailable', field: false };
    default:
      return { key: 'licensePage.errors.generic', field: false };
  }
}

/**
 * Settings → Licence (/settings/license): the user's current licence (plan,
 * status, valid until — the free beta licence expires after
 * BETA_AUTO_LICENSE_DAYS, default 90), a warning from 14 days before expiry,
 * and a form to activate a licence key (POST /api/v1/licenses/activate, or
 * PUT /exchange while a licence is active). Reachable without a licence.
 */
export function LicensePage() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const keyFieldRef = useRef<HTMLTextAreaElement>(null);

  const { data: license, isLoading, isError, refetch, isRefetching } = useQuery({
    queryKey: ownLicenseQueryKey,
    queryFn: fetchOwnLicense,
  });

  const [key, setKey] = useState('');
  const [fieldError, setFieldError] = useState<string | undefined>();
  const [formError, setFormError] = useState<string | null>(null);
  const [success, setSuccess] = useState<OwnLicense | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const isActive = license?.status === 'active';
  const lang = i18n.language;

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setFormError(null);
    setSuccess(null);
    const code = key.replace(/\s+/g, '');
    if (code === '') {
      setFieldError(t('licensePage.errors.required'));
      keyFieldRef.current?.focus();
      return;
    }
    setFieldError(undefined);
    setSubmitting(true);
    try {
      const activated = await applyLicenseKey(code, isActive);
      setKey('');
      setSuccess(activated);
      clearLicenseRequired();
      queryClient.setQueryData(ownLicenseQueryKey, activated);
      // Licence-gated data (aquariums, AI credits …) may have failed before.
      await queryClient.invalidateQueries();
    } catch (err) {
      const { key: messageKey, field } = errorKey(err);
      if (field) {
        setFieldError(t(messageKey));
        keyFieldRef.current?.focus();
      } else {
        setFormError(t(messageKey));
        if (err instanceof ApiError && (err.code === 'USER_ALREADY_LICENSED' || err.code === 'NO_ACTIVE_LICENSE')) {
          void refetch();
        }
      }
    } finally {
      setSubmitting(false);
    }
  }

  function renderStatusNotice(current: OwnLicense) {
    const days = daysUntil(current.expiresAt);
    const date = formatLongDate(current.expiresAt, lang);
    if (current.status === 'revoked') {
      return (
        <Alert tone="danger" live="off" title={t('licensePage.revokedTitle')}>
          {t('licensePage.revokedMessage')}
        </Alert>
      );
    }
    if (current.status === 'expired') {
      return (
        <Alert tone="warning" live="off" title={t('licensePage.expiredTitle', { date })}>
          {current.plan === 'beta' ? t('licensePage.expiredBetaMessage') : t('licensePage.expiredMessage')}
        </Alert>
      );
    }
    if (days !== null && days <= LICENSE_EXPIRY_WARNING_DAYS) {
      return (
        <Alert tone="warning" live="off" title={t('licensePage.expiringTitle', { date, count: Math.max(days, 0) })}>
          {current.plan === 'beta' ? t('licensePage.expiringBetaMessage') : t('licensePage.expiringMessage')}
        </Alert>
      );
    }
    return null;
  }

  function statusTone(status: OwnLicense['status']): 'success' | 'warning' | 'danger' {
    if (status === 'active') return 'success';
    if (status === 'expired') return 'warning';
    return 'danger';
  }

  return (
    <AppLayout
      header={<Header />}
      sidebar={<Sidebar />}
      main={
        <PageContent width="narrow">
          <PageHeader title={t('licensePage.title')} description={t('licensePage.subtitle')} />

          <Section title={t('licensePage.currentTitle')}>
            {isLoading && (
              <LiveRegion>
                <Text>{t('licensePage.loading')}</Text>
              </LiveRegion>
            )}

            {isError && (
              <LoadErrorState title={t('licensePage.loadError')} onRetry={() => void refetch()} retrying={isRefetching} />
            )}

            {!isLoading && !isError && (license ?? null) === null && (
              <EmptyState
                icon={<KeyRound size={48} strokeWidth={1.5} aria-hidden="true" />}
                headline={t('licensePage.noneTitle')}
                description={t('licensePage.noneMessage')}
                actionLabel={t('licensePage.enterKey')}
                onAction={() => keyFieldRef.current?.focus()}
                actionVariant="secondary"
                headingLevel={3}
              />
            )}

            {!isLoading && !isError && license != null && (
              <Stack gap="4">
                {renderStatusNotice(license)}
                <Card padding="lg">
                  <dl className="rg-license-page__details">
                    <div className="rg-license-page__detail">
                      <dt>{t('licensePage.status')}</dt>
                      <dd>
                        <Badge tone={statusTone(license.status)}>{t(`licensePage.statusValue.${license.status}`)}</Badge>
                      </dd>
                    </div>
                    <div className="rg-license-page__detail">
                      <dt>{t('licensePage.type')}</dt>
                      <dd>{String(t(`licensePage.plan.${license.plan}`, { defaultValue: t('licensePage.plan.standard') }))}</dd>
                    </div>
                    <div className="rg-license-page__detail">
                      <dt>{t('licensePage.validUntil')}</dt>
                      <dd>
                        {license.expiresAt === null
                          ? t('licensePage.unlimited')
                          : formatLongDate(license.expiresAt, lang)}
                      </dd>
                    </div>
                    <div className="rg-license-page__detail">
                      <dt>{t('licensePage.activatedAt')}</dt>
                      <dd>{formatLongDate(license.activatedAt, lang)}</dd>
                    </div>
                  </dl>
                  {license.plan === 'beta' && license.status === 'active' && (
                    <Text variant="muted" className="rg-license-page__note">{t('licensePage.betaNote')}</Text>
                  )}
                </Card>
              </Stack>
            )}
          </Section>

          <Section
            title={isActive ? t('licensePage.exchangeTitle') : t('licensePage.activateTitle')}
            description={isActive ? t('licensePage.exchangeDescription') : t('licensePage.activateDescription')}
          >
            <Card padding="lg">
              <form onSubmit={(e) => void onSubmit(e)} noValidate>
                <Stack gap="4">
                  <Textarea
                    ref={keyFieldRef}
                    label={t('licensePage.keyLabel')}
                    helperText={t('licensePage.keyHint')}
                    value={key}
                    onChange={(e) => setKey(e.target.value)}
                    error={fieldError}
                    rows={3}
                    autoComplete="off"
                    spellCheck={false}
                    fullWidth
                  />
                  {formError !== null && <Alert>{formError}</Alert>}
                  {success !== null && (
                    <Alert tone="success" title={t('licensePage.successTitle')}>
                      {success.expiresAt === null
                        ? t('licensePage.successUnlimited')
                        : t('licensePage.successUntil', { date: formatLongDate(success.expiresAt, lang) })}
                    </Alert>
                  )}
                  <div>
                    <Button type="submit" isLoading={submitting} disabled={submitting}>
                      {isActive ? t('licensePage.submitExchange') : t('licensePage.submitActivate')}
                    </Button>
                  </div>
                </Stack>
              </form>
            </Card>
            <Text variant="muted">{t('licensePage.supportHint')}</Text>
          </Section>
        </PageContent>
      }
    />
  );
}

export default LicensePage;
